import xlsx from 'xlsx';

export default function importExcel(f) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader(); // eslint-disable-line
    const rABS = true; // true: readAsBinaryString ; false: readAsArrayBuffer

    // eslint-disable-next-line consistent-return
    reader.onload = (e) => {
      let data = e.target.result;
      if (!rABS) {
        data = new Uint8Array(data);
      }
      const workbook = xlsx.read(data, { type: rABS ? 'binary' : 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const converted = xlsx.utils.sheet_to_json(sheet);
      const csvRows = xlsx.utils.sheet_to_csv(sheet);
      const columnNames = csvRows.split(/\n/)[0].split(',').map((colName) => colName.trim().toLowerCase());
      if (!columnNames.includes('kundennummer')) {
        return reject(new Error('trialmanagement.excelImportMissingKundennummer'));
      }
      if (!columnNames.includes('setnummer') && !columnNames.includes('groundtruth')) {
        return reject(new Error('trialmanagement.excelImportMissingColumn'));
      }

      const setDeliverySetId = columnNames.includes('setnummer');
      const setGroundTruthdata = columnNames.includes('groundtruth');
      const selection = converted.map((row, index) => {
        const trimmedRow = Object.entries(row)
          .map(([key, value]) => ([key.trim().toLowerCase(), value]))
          .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});

        if (!trimmedRow.kundennummer) {
          const errObj = new Error('trialmanagement.emptyKundennummer');
          // plus 2 because of the header row and the 0-index
          errObj.data = { rowNumber: index + 2 };
          return reject(errObj);
        }

        const newRow = {
          quipUserId: Number(trimmedRow.kundennummer.split('Q')[1] || trimmedRow.kundennummer.split('I')[1]),
        };

        if (setDeliverySetId) {
          newRow.deliverySetId = Number(trimmedRow.setnummer);
        }

        if (setGroundTruthdata) {
          newRow.groundtruth = Number(trimmedRow.groundtruth);
        }
        return newRow;
      });
      resolve(selection);
    };

    if (rABS) {
      reader.readAsBinaryString(f);
    } else {
      reader.readAsArrayBuffer(f);
    }
  });
}

export async function importVouchers(f) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader(); // eslint-disable-line
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = xlsx.read(data, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const csvRows = xlsx.utils.sheet_to_csv(sheet, { FS: ',', blankrows: false, strip: true });
      const codeList = csvRows.trim().split(/\n/);

      let codeArray = [];
      codeArray = codeList.map((code) => {
        const c = code.trim();
        if (c.length !== 11) {
          reject(new Error('INVALID_CODE_LENGTH'));
        }
        if (c.charAt(4) !== '-') {
          reject(new Error('NO_DASH_FOUND'));
        }
        return c;
      });
      resolve(codeArray);
    };
    reader.readAsArrayBuffer(f);
  });
}

function parseMultiblockSheet(sheet) {
  const csvRows = xlsx.utils.sheet_to_csv(sheet, { FS: '\t', blankrows: false, strip: true });
  const rows = csvRows.split(/\n/);
  const colRows = rows.map((r) => r.split(/\t/));
  const groupedArr = [];
  let currentKey = '';
  let index = -1;
  colRows.forEach((row) => {
    if (row.length !== 4) {
      return;
    }
    const key = row[1];
    if (key.trim() !== '' && currentKey !== key) {
      currentKey = key;
      index += 1;
      groupedArr[index] = [];
    }
    groupedArr[index].push(row);
  });
  const processedArr = groupedArr.map((group) => {
    const obj = group.reduce((acc, current) => {
      const currentRow = current.map((r) => r.trim());
      const [text, key, code, comment] = currentRow;
      if (text) {
        acc.text = text;
      }
      if (key) {
        acc.key = key;
      }
      if (!acc.options) {
        acc.options = {};
      }
      acc.options[code] = comment;
      return acc;
    }, {});
    return obj;
  });
  return processedArr;
}

export function importMultiBlockComments(f) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = xlsx.read(data, { type: 'array' });
      const sheetNames = workbook.SheetNames;
      const commentData = {};
      sheetNames.forEach((sheetName, idx) => {
        const sheet = workbook.Sheets[sheetName];
        const jsonSheet = parseMultiblockSheet(sheet);
        commentData[`section${idx + 1}`] = jsonSheet;
      });
      resolve(commentData);
    };
    reader.readAsArrayBuffer(f);
  });
}

const EMAIL_COLUMN_KEY = 'email';

/**
 * Parse a CSV/Excel file and return unique emails from the "email" column.
 * Column name is matched case-insensitively after trimming.
 * @param {File} file - uploaded file
 * @returns {Promise<string[]>} unique non-empty emails
 */
export function parseBulkImportEmails(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = xlsx.read(data, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const converted = xlsx.utils.sheet_to_json(sheet);
        const csvRows = xlsx.utils.sheet_to_csv(sheet);
        const columnNames = csvRows.split(/\n/)[0].split(',').map((col) => col.trim().toLowerCase());
        const emailColIndex = columnNames.findIndex((c) => c === EMAIL_COLUMN_KEY);
        if (emailColIndex === -1) {
          return reject(new Error('internalTrial.bulkImportMissingEmailColumn'));
        }
        const emailKey = columnNames[emailColIndex];
        const emails = new Set();
        converted.forEach((row) => {
          const trimmedRow = Object.entries(row)
            .map(([key, value]) => [key.trim().toLowerCase(), value])
            .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
          const val = trimmedRow[emailKey];
          const email = (val != null ? String(val) : '').trim().toLowerCase();
          if (email) {
            emails.add(email);
          }
        });
        return resolve([...emails]);
      } catch (err) {
        return reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

const QID_COLUMN_KEYS = ['quip id', 'qid', 'quipid'];

/**
 * Parse Phase 2 format XLSX and return unique user IDs from the QID column (Matched sheet).
 * Uses sheet named "Matched" if present, otherwise first sheet.
 * @param {File} file - uploaded file (Phase 2 output)
 * @param {function(string): number} parseQidToUserId - converts QID string to userId
 * @returns {Promise<number[]>} unique valid user IDs
 */
export function parseBulkEnrollQids(file, parseQidToUserId) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = xlsx.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames.find((n) => n.trim().toLowerCase() === 'matched') || workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const converted = xlsx.utils.sheet_to_json(sheet);
        const csvRows = xlsx.utils.sheet_to_csv(sheet);
        const columnNames = csvRows.split(/\n/)[0].split(',').map((c) => c.trim().toLowerCase());
        const qidKey = columnNames.find((c) => QID_COLUMN_KEYS.includes(c));
        if (!qidKey) {
          return reject(new Error('internalTrial.bulkEnrollMissingQidColumn'));
        }
        const userIds = new Set();
        let hasInvalid = false;
        converted.forEach((row) => {
          const trimmedRow = Object.entries(row)
            .map(([key, value]) => [key.trim().toLowerCase(), value])
            .reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {});
          const val = trimmedRow[qidKey];
          const str = val != null ? String(val).trim() : '';
          if (!str) {
            return;
          }
          const id = parseQidToUserId(str);
          if (Number.isNaN(id) || id <= 0) {
            hasInvalid = true;
            return;
          }
          userIds.add(id);
        });
        if (hasInvalid && userIds.size === 0) {
          return reject(new Error('internalTrial.bulkEnrollNoValidQids'));
        }
        return resolve([...userIds]);
      } catch (err) {
        return reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}
